import { useIntl } from "react-intl";
import { Grid, useMediaQuery } from "@mui/material";
import { Visibility } from "@mui/icons-material";

import { SelectInput } from "@dzambalaorg/mui-inputs-core";
import { SearchWithFilters } from "@dzambalaorg/mui-form-search";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { ListAction, ListActions, StyledListWrapper } from "@framework/styled";
import { type IPonziRule, TokenType } from "@framework/types";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { useDialogView, useFetchQuery } from "@dzambalaorg/react-hooks";
import { List, Pagination } from "@dzambalaorg/ui-kit";

import { PonziDepositButton } from "../../../../../components/buttons";
import { PonziDialogView } from "./view";

const PONZI_RULES_URL = "/ponzi/rules";
const PONZI_RULES_VIEW_DIALOG_KEY = "ponziRules";

export const PonziRules = () => {
  const isMd = useMediaQuery(theme => theme.breakpoints.down("md"));

  const { isLoading, take, skip, onChangePage, hasNextPage, loadMoreRef, allRows, totalCount } = useFetchQuery<
    IPaginationResult<IPonziRule>
  >(PONZI_RULES_URL, { enableInfiniteScroll: isMd });

  const { viewItem, handleView, viewDialogUniqueKey, closeViewDialog, viewMode } = useDialogView<IPonziRule>();

  const { formatMessage } = useIntl();

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "ponzi", "ponzi.rules"]} />

      <PageHeader message="pages.ponzi.rules.title" />

      <SearchWithFilters>
        <Grid container columnSpacing={2} alignItems="flex-end">
          <Grid size={{ xs: 6 }}>
            <SelectInput
              multiple
              name="deposit.tokenType"
              options={TokenType}
              label={formatMessage({ id: "form.labels.deposit" })}
              disabledOptions={[TokenType.ERC721, TokenType.ERC998, TokenType.ERC1155]}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <SelectInput
              multiple
              name="reward.tokenType"
              options={TokenType}
              label={formatMessage({ id: "form.labels.reward" })}
              disabledOptions={[TokenType.ERC721, TokenType.ERC998, TokenType.ERC1155]}
            />
          </Grid>
        </Grid>
      </SearchWithFilters>

      <ProgressOverlay isLoading={isLoading}>
        <StyledListWrapper count={allRows.length} isLoading={isLoading}>
          {allRows.map(rule => (
            <List.Item key={rule.id}>
              <List.ItemText>{rule.title}</List.ItemText>
              <ListActions>
                <PonziDepositButton rule={rule} />
                <ListAction
                  onClick={handleView(PONZI_RULES_VIEW_DIALOG_KEY, rule, "view")}
                  message="form.tips.view"
                  icon={Visibility}
                />
              </ListActions>
            </List.Item>
          ))}
        </StyledListWrapper>
      </ProgressOverlay>

      {!isMd && totalCount > take && (
        <Pagination
          shape="rounded"
          page={skip / take + 1}
          count={Math.ceil(totalCount / take)}
          onChange={onChangePage}
        />
      )}
      {isMd && hasNextPage && <div ref={loadMoreRef} style={{ height: "20px", margin: "20px 0" }} />}

      <PonziDialogView
        onCancel={closeViewDialog}
        onConfirm={closeViewDialog}
        open={viewDialogUniqueKey === `${PONZI_RULES_VIEW_DIALOG_KEY}.${viewMode}`}
        viewItem={viewItem}
      />
    </Grid>
  );
};
